/**
 * Runtime connection config derived from URL params.
 *
 * Local mode: no ?robot_id — relative URLs go directly to robot fastapi
 * Hub mode:   ?robot_id=xxx — all requests go to HUB_URL
 */

import { HUB_URL } from './constants';

const params = new URLSearchParams(window.location.search);

export const robotId: string | null = params.get('robot_id') ?? null;

function appendQuery(url: string, params: Record<string, string | null | undefined>): string {
  const entries = Object.entries(params).filter((entry): entry is [string, string] => Boolean(entry[1]));
  if (entries.length === 0) return url;
  const query = entries.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`).join('&');
  return `${url}${url.includes('?') ? '&' : '?'}${query}`;
}

/** Prefix a REST path for the current mode. */
export function apiPath(path: string): string {
  return robotId ? `${HUB_URL}/robots/${robotId}${path}` : path;
}

/** Build the WebSocket URL for the current mode. */
export function wsUrl(): string {
  if (!robotId) {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}/data/ws`;
  }
  const wsBase = HUB_URL.replace(/^http/, 'ws');
  return `${wsBase}/ws/${robotId}`;
}

/** The robot's own MJPEG feed for one camera. Local mode only. */
export function cameraFeedUrl(cameraId: string): string {
  return `/data/camera/feed/${cameraId}`;
}

/** Absolute URL for a generated .rrd that the in-page Rerun web viewer fetches itself.
 *  Local mode: same-origin absolute. Hub mode: hub URL with `?token=` — the viewer can't
 *  set an Authorization header. */
export function rerunFileUrl(path: string, token?: string | null): string {
  if (robotId) return appendQuery(`${HUB_URL}/robots/${robotId}${path}`, { token });
  return `${window.location.origin}${path}`;
}

/** Absolute URL for one rendition of a task attachment, which an `<img>` fetches
 *  itself. Hub mode carries the bearer as `?token=` for the same reason the
 *  Rerun files do — an `<img>` cannot set an Authorization header.
 *
 *  `display` is a downscaled WEBP and is what the panel shows; `original` is for
 *  the full-screen viewer alone. A robot serves these over its own uplink, so
 *  asking for the original by default would cost megabytes per photo. */
export function taskAttachmentUrl(
  taskName: string,
  attachmentId: number,
  variant: 'display' | 'original' = 'display',
  token?: string | null,
): string {
  const path = `/operator/task/${encodeURIComponent(taskName)}/attachments/${attachmentId}/content`;
  const url = robotId ? `${HUB_URL}/robots/${robotId}${path}` : path;
  return appendQuery(url, { variant, token: robotId ? token : null });
}

/** The release URL for a local MJPEG session (sent by sendBeacon on unmount). */
export function cameraReleaseUrl(cameraId: string, sessionId: string): string {
  return `/data/camera/release/${cameraId}/${sessionId}`;
}
